import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel, ShieldCheck, MapPin, Briefcase, CaretRight, UserCircle, Plus, Envelope } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ['Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'];
const LANGUAGES = ['Telugu', 'Hindi', 'Tamil', 'Malayalam', 'Kannada', 'English'];

const Fraternity = ({ user }) => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: '',
    role: '',
    industry: '',
    location: '',
    language: '',
    open_for_work: false,
    verified_only: false,
    min_years: '',
    min_credits: '',
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/fraternity/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === false || v === null) return;
        params[k] = v;
      });
      const res = await axios.get(`${BACKEND_URL}/api/fraternity/directory`, { params });
      setResults(res.data.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const initials = (label) => (label || '?').split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {/* Top nav */}
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-2xl font-extrabold tracking-tight" data-testid="brand-link">
            Fan<span style={{ background: 'linear-gradient(90deg,#f472b6,#c084fc,#22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Zingy</span>
          </button>
          <div className="flex items-center gap-3">
            {user && (
              <button onClick={() => navigate('/fraternity/inbox')} className="text-sm text-white/80 hover:text-white flex items-center gap-1.5" data-testid="nav-inbox">
                <Envelope size={16} /> Inbox
              </button>
            )}
            {user ? (
              <button onClick={() => navigate('/fraternity/me/edit')} className="bg-white text-black font-bold text-sm px-4 py-2 rounded-full hover:scale-105 transition-transform flex items-center gap-1.5" data-testid="my-profile-btn">
                <UserCircle size={16} weight="bold" /> My profile
              </button>
            ) : (
              <button onClick={() => navigate('/login')} className="bg-white text-black font-bold text-sm px-4 py-2 rounded-full" data-testid="sign-in-btn">Sign in</button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/15 rounded-full mb-4">
          <Briefcase size={14} weight="fill" className="text-[#facc15]" />
          <span className="text-[11px] font-semibold uppercase tracking-wider">Fanzingy Fraternity</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-3" style={{ letterSpacing: '-0.03em' }} data-testid="fraternity-title">
          The film industry,{' '}
          <span style={{ background: 'linear-gradient(90deg,#f472b6,#c084fc,#22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>connected.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg max-w-2xl font-light">
          Find directors, DOPs, actors, editors and every craft in between. Tag them onto your next project, endorse the ones you've worked with, and signal that you're open to work.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 backdrop-blur-md" data-testid="fraternity-filters">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className="relative md:col-span-2">
              <MagnifyingGlass aria-hidden="true" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70" />
              <input
                data-testid="filter-q"
                aria-label="Search by name or keyword"
                placeholder="Search name, headline, role…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                className="w-full bg-black/40 border border-white/15 rounded-lg pl-10 pr-3 py-2.5 text-sm placeholder:text-white/50 focus:border-[#c084fc] outline-none"
              />
            </div>
            <select
              data-testid="filter-role"
              aria-label="Filter by role"
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#c084fc] outline-none"
            >
              <option value="">All roles</option>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              data-testid="filter-industry"
              aria-label="Filter by industry"
              value={filters.industry}
              onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#c084fc] outline-none"
            >
              <option value="">All industries</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <input
              data-testid="filter-location"
              aria-label="Filter by location"
              placeholder="Location"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm placeholder:text-white/50 focus:border-[#c084fc] outline-none"
            />
            <select
              data-testid="filter-language"
              aria-label="Filter by language"
              value={filters.language}
              onChange={(e) => setFilters({ ...filters, language: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-[#c084fc] outline-none"
            >
              <option value="">Any language</option>
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <input
              data-testid="filter-min-years"
              aria-label="Minimum years of experience"
              type="number"
              min="0"
              placeholder="Min years"
              value={filters.min_years}
              onChange={(e) => setFilters({ ...filters, min_years: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm placeholder:text-white/50 focus:border-[#c084fc] outline-none"
            />
            <input
              data-testid="filter-min-credits"
              aria-label="Minimum number of credits"
              type="number"
              min="0"
              placeholder="Min IMDB credits"
              value={filters.min_credits}
              onChange={(e) => setFilters({ ...filters, min_credits: e.target.value })}
              className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm placeholder:text-white/50 focus:border-[#c084fc] outline-none"
            />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer" data-testid="filter-open-for-work-label">
                <input type="checkbox" data-testid="filter-open-for-work" checked={filters.open_for_work} onChange={(e) => setFilters({ ...filters, open_for_work: e.target.checked })} />
                Open
              </label>
              <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" data-testid="filter-verified-only" checked={filters.verified_only} onChange={(e) => setFilters({ ...filters, verified_only: e.target.checked })} />
                Verified
              </label>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/70">
              {loading ? 'Searching…' : `${results.length} ${results.length === 1 ? 'member' : 'members'}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid="filters-reset"
                onClick={() => setFilters({ q: '', role: '', industry: '', location: '', language: '', open_for_work: false, verified_only: false, min_years: '', min_credits: '' })}
                className="text-xs text-white/70 hover:text-white"
              >
                Reset
              </button>
              <button
                data-testid="filters-apply"
                onClick={search}
                className="bg-white text-black font-bold text-xs px-4 py-2 rounded-full flex items-center gap-1.5 hover:scale-105 transition-transform"
              >
                <Funnel size={14} weight="bold" /> Apply filters
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {results.length === 0 ? (
          <div className="bg-white/5 border border-dashed border-white/15 rounded-xl p-12 text-center" data-testid="empty-state">
            <UserCircle size={48} className="mx-auto text-white/70 mb-3" />
            <h3 className="text-lg font-bold mb-2">No members match yet</h3>
            <p className="text-sm text-white/70 mb-4">Be the first — or refine your filters.</p>
            {user && (
              <button
                data-testid="empty-create-profile"
                onClick={() => navigate('/fraternity/me/edit')}
                className="bg-white text-black font-bold text-sm px-5 py-2 rounded-full inline-flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <Plus size={14} weight="bold" /> Create your profile
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fraternity-grid">
            {results.map((p, idx) => (
              <motion.button
                key={p.user_id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                onClick={() => navigate(`/fraternity/u/${p.user_id}`)}
                data-testid={`fraternity-card-${p.user_id}`}
                className="text-left bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#c084fc]/50 hover:bg-white/[0.08] transition-all group"
              >
                <div className="flex items-start gap-3 mb-3">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.display_name} className="w-14 h-14 rounded-lg object-cover ring-1 ring-white/15" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#f472b6] via-[#c084fc] to-[#22d3ee] flex items-center justify-center font-extrabold text-black">
                      {initials(p.display_name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-extrabold leading-tight truncate group-hover:text-[#c084fc] transition-colors" data-testid={`card-name-${p.user_id}`}>{p.display_name}</div>
                      {p.verified && <ShieldCheck size={14} weight="fill" className="text-[#22d3ee] flex-shrink-0" data-testid={`verified-badge-${p.user_id}`} />}
                    </div>
                    <div className="text-[11px] text-[#facc15] uppercase tracking-wider font-bold mt-0.5 truncate">{p.primary_role}</div>
                    {p.headline && <div className="text-xs text-white/60 mt-1 line-clamp-2 font-light">{p.headline}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-white/70 flex-wrap">
                  {p.location && <span className="flex items-center gap-1"><MapPin size={11} />{p.location}</span>}
                  {p.years_experience > 0 && <span>{p.years_experience} yrs</span>}
                  {p.endorsement_count > 0 && <span>★ {p.endorsement_count}</span>}
                  {p.open_for_work && (
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 uppercase tracking-wider font-bold" data-testid={`open-pill-${p.user_id}`}>Open</span>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-white/70">View profile</span>
                  <CaretRight size={14} className="text-white/70 group-hover:text-[#c084fc] transition-colors" />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Fraternity;
